import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback
} from 'react';
import request from '../utils/request';

const AuthContext = createContext(null);
const AUTH_EXPIRE_DAYS = 7;
const AUTH_EXPIRE_MS = AUTH_EXPIRE_DAYS * 24 * 60 * 60 * 1000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState('');
  const [authChecked, setAuthChecked] = useState(false);

  const clearAuthStorage = useCallback(() => {
    localStorage.removeItem('user_info');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_expires_at');
    localStorage.removeItem('device_id');
    localStorage.removeItem('device_type');
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setToken('');
    clearAuthStorage();
    setAuthChecked(true);
  }, [clearAuthStorage]);

  const setUserInfo = useCallback((userInfo, authToken, expiresAtFromServer) => {
    const expiresAt =
      expiresAtFromServer || Date.now() + AUTH_EXPIRE_MS;

    setUser(userInfo);
    setToken(authToken);

    localStorage.setItem('user_info', JSON.stringify(userInfo));
    localStorage.setItem('auth_token', authToken);
    localStorage.setItem('auth_expires_at', String(expiresAt));
  }, []);

  const validateSession = useCallback(async (savedToken) => {
    try {
      const response = await request.get('/api/auth/me', {
        headers: {
          Authorization: `Bearer ${savedToken}`,
        },
      });

      if (response.data?.success && response.data?.user && response.data?.token) {
        const nextUser = response.data.user;
        const nextToken = response.data.token;
        const nextExpiresAt = response.data.expiresAt;

        setUser(nextUser);
        setToken(nextToken);

        localStorage.setItem('user_info', JSON.stringify(nextUser));
        localStorage.setItem('auth_token', nextToken);

        if (nextExpiresAt) {
          localStorage.setItem('auth_expires_at', String(nextExpiresAt));
        }

        return true;
      }

      logout();
      return false;
    } catch (error) {
      logout();
      return false;
    } finally {
      setAuthChecked(true);
    }
  }, [logout]);

  useEffect(() => {
    const initAuth = async () => {
      const storedUser = localStorage.getItem('user_info');
      const storedToken = localStorage.getItem('auth_token');
      const storedExpiresAt = localStorage.getItem('auth_expires_at');

      if (!storedUser || !storedToken || !storedExpiresAt) {
        setAuthChecked(true);
        return;
      }

      const expiresAt = Number(storedExpiresAt);

      if (!expiresAt || Date.now() > expiresAt) {
        logout();
        return;
      }

      try {
        const parsedUser = JSON.parse(storedUser);

        setUser(parsedUser);
        setToken(storedToken);

        await validateSession(storedToken);
      } catch (error) {
        console.error('解析用户信息失败:', error);
        logout();
      }
    };

    initAuth();
  }, [logout, validateSession]);

  const value = useMemo(() => ({
    user,
    token,
    isAuthenticated: !!user && !!token,
    authChecked,
    setUserInfo,
    logout,
    validateSession,
  }), [user, token, authChecked, setUserInfo, logout, validateSession]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }

  return context;
}